// A text cut short shows itself whole in a tooltip when the mouse rests on it
// (the person, 2026-11-02): a list row's name (`.fade-end`) and any one-line
// text cut with an ellipsis (`truncate`), in every list and sidebar alike. A
// text that fits shows nothing.
//
// One listener for the whole document, so no list wires it row by row. The
// text is found under the resting pointer by its place, not by the event's
// target: a row's link covers its whole row over its name (`ListRow`), so the
// pointer is never on the name itself. Whether the text is cut is read only
// then. The look and the delay are `<Tooltip>`'s. It sits over the text,
// starting where the text starts, so the cut words go on where they were cut;
// a text too long for a line wraps. It hides when the pointer leaves the
// text, on a press (and stays away from that text until the pointer leaves
// it), scroll, Escape and the window losing focus. A finger never shows it:
// on a touch screen a cut name scrolls sideways instead (styles/index.css).

const CUT = ".fade-end, .truncate";
const DELAY_MS = 450;
const TIP =
  "pointer-events-none fixed z-[60] max-w-[min(480px,calc(100vw-16px))] break-words rounded-[6px] bg-fg px-2 py-1 text-[11.5px] text-surface";

export function installCutTextTooltip(target: Document = document): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let tip: { node: HTMLDivElement; on: HTMLElement } | null = null;
  // The text pressed on: no tooltip again until the pointer leaves it.
  let pressed: HTMLElement | null = null;
  let x = 0;
  let y = 0;
  const cancel = () => {
    if (timer) clearTimeout(timer);
    timer = null;
  };
  const hide = () => {
    cancel();
    tip?.node.remove();
    tip = null;
  };
  const cutAt = (): HTMLElement | null => {
    for (const n of target.elementsFromPoint(x, y)) {
      const el = n.closest(CUT);
      if (el instanceof HTMLElement) return el;
    }
    return null;
  };
  const within = (el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    return x >= r.left && x < r.right && y >= r.top && y < r.bottom;
  };
  const show = (el: HTMLElement) => {
    const text = el.textContent?.trim();
    if (!text) return;
    const node = target.createElement("div");
    node.setAttribute("role", "tooltip");
    node.setAttribute("aria-hidden", "true");
    node.className = TIP;
    node.textContent = text;
    target.body.append(node);
    // Over the text when there is room above it, else under it; slid back
    // inside the window near its edges.
    const r = el.getBoundingClientRect();
    const t = node.getBoundingClientRect();
    const room = target.documentElement.clientWidth - 8;
    const left = Math.max(8, Math.min(r.left - 8, room - t.width));
    const top = r.top - 6 - t.height >= 8 ? r.top - 6 - t.height : r.bottom + 6;
    node.style.left = `${left}px`;
    node.style.top = `${top}px`;
    tip = { node, on: el };
  };
  const rest = () => {
    timer = null;
    const el = cutAt();
    if (el !== pressed) pressed = null;
    if (!el || el === pressed) return;
    if (el.clientWidth === 0 || el.scrollWidth <= el.clientWidth) return;
    show(el);
  };
  const onMove = (e: PointerEvent) => {
    if (e.pointerType !== "mouse") return;
    x = e.clientX;
    y = e.clientY;
    if (tip) {
      if (tip.on.isConnected && within(tip.on)) return;
      hide();
    }
    cancel();
    timer = setTimeout(rest, DELAY_MS);
  };
  const onDown = () => {
    hide();
    pressed = cutAt();
  };
  const onOut = (e: PointerEvent) => {
    // Out of the window: no move follows.
    if (e.relatedTarget === null) hide();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") hide();
  };
  const opts = { capture: true, passive: true } as const;
  target.addEventListener("pointermove", onMove, opts);
  target.addEventListener("pointerout", onOut, opts);
  target.addEventListener("pointerdown", onDown, opts);
  target.addEventListener("scroll", hide, opts);
  target.addEventListener("keydown", onKey, opts);
  target.defaultView?.addEventListener("blur", hide);
  return () => {
    hide();
    target.removeEventListener("pointermove", onMove, opts);
    target.removeEventListener("pointerout", onOut, opts);
    target.removeEventListener("pointerdown", onDown, opts);
    target.removeEventListener("scroll", hide, opts);
    target.removeEventListener("keydown", onKey, opts);
    target.defaultView?.removeEventListener("blur", hide);
  };
}
